import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '@/i18n';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Catches render errors (and failed lazy-chunk loads) below it so a single broken
// page shows a recoverable message instead of unmounting the whole app. Styles are
// inline on purpose: the fallback must render even if a page stylesheet failed to load.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('Unhandled render error:', error, info.componentStack);
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const t = (key: string) => i18n.t(`errorBoundary.${key}`, { ns: 'common' });
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h1 style={{ fontSize: 28, fontWeight: 600, margin: 0 }}>{t('title')}</h1>
        <p style={{ maxWidth: 480, margin: 0, opacity: 0.8 }}>{t('description')}</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#1b6504',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('reload')}
          </button>
          <a
            href="/"
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: '1px solid currentColor',
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            {t('home')}
          </a>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
